import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { LOCALIZED_STRINGS, MAP_SIZE_DESKTOP, MAP_SIZE_MOBILE } from './constants';
import GoogleStaticMap from '@zocdoc/components/src/GoogleStaticMap';
import { breakpoint } from '@zocdoc/components/src/grid';
import { linkStyle } from '@zocdoc/components/src/link';
import { FontFamily } from '@zocdoc/components/src/typography';
import { FormattedMessage } from 'react-intl';
import styled, { withTheme } from 'styled-components';
import { get } from 'lodash';

const mapMarginMobile = 14;
const mapMarginDesktop = 23;

export class LocationCard extends PureComponent {
    static propTypes = {
        name: PropTypes.string.isRequired,
        address1: PropTypes.string.isRequired,
        address2: PropTypes.string,
        city: PropTypes.string.isRequired,
        state: PropTypes.string.isRequired,
        zipCode: PropTypes.string.isRequired,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        onToggleShowMore: PropTypes.func,
        showingMore: PropTypes.bool,
        onClickExpandMap: PropTypes.func,
        showMapThumbnail: PropTypes.bool,
        theme: PropTypes.object,
        UseMarkdownForLocationInfoCard: PropTypes.string,
    };

    static defaultProps = {
        showMapThumbnail: true,
    };

    renderAddress() {
        const {
            address1,
            address2,
        } = this.props;

        if (address2) {
            return (
                <p
                    data-test="address"
                    itemProp="streetAddress"
                >
                    {address1}, {address2}
                </p>
            );
        } else {
            return (
                <p
                    data-test="address"
                    itemProp="streetAddress"
                >
                    {address1}
                </p>
            );
        }
    }

    renderCityStateZip() {
        const {
            city,
            state,
            zipCode,
        } = this.props;
        return (
            <p data-test="city-state-zip">
                <span itemProp="addressLocality">{city}</span>, <span itemProp="addressRegion">{state}</span> <span itemProp="postalCode">{zipCode}</span>
            </p>
        );
    }

    renderShowMore(onToggleShowMore) {
        return (
            <Button
                data-test="show-more"
                onClick={onToggleShowMore}
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ShowMore} />
            </Button>
        );
    }

    renderShowLess(onToggleShowMore) {
        return (
            <Button
                data-test="show-less"
                onClick={onToggleShowMore}
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ShowLess} />
            </Button>
        );
    }

    maybeRenderShowMoreShowLess() {
        const { theme, onToggleShowMore, showingMore } = this.props;
        const locInfo = get(theme, 'profile.locationInfo.markdown');

        if (onToggleShowMore && locInfo) {
            if (showingMore) {
                return this.renderShowLess(onToggleShowMore);
            } else {
                return this.renderShowMore(onToggleShowMore);
            }
        }
    }

    maybeRenderMapThumbnail() {
        const {
            showMapThumbnail, latitude, longitude, onClickExpandMap,
        } = this.props;

        if (showMapThumbnail) {
            return (
                <MapContainer>
                    <MobileMap data-test="location-card-map-mobile">
                        <GoogleStaticMap
                            height={MAP_SIZE_MOBILE}
                            latitude={latitude}
                            longitude={longitude}
                            onClick={onClickExpandMap}
                            width={MAP_SIZE_MOBILE}
                        />
                    </MobileMap>
                    <DesktopMap data-test="location-card-map-desktop">
                        <GoogleStaticMap
                            height={MAP_SIZE_DESKTOP}
                            latitude={latitude}
                            longitude={longitude}
                            onClick={onClickExpandMap}
                            width={MAP_SIZE_DESKTOP}
                        />
                    </DesktopMap>
                </MapContainer>
            );
        }
    }

    maybeRenderPracticeName() {
        const { name, practiceName } = this.props;

        if (practiceName && practiceName !== name) {
            return (
                <LocationName data-test="practice-name">{practiceName}</LocationName>
            );
        }
    }

    render() {
        const {
            name,
            latitude,
            longitude,
        } = this.props;

        return (
            <StyledLocationCard
                data-test="location-card"
                itemProp="location"
                itemScope
                itemType="http://schema.org/Place"
            >
                <div
                    itemProp="geo"
                    itemScope
                    itemType="http://schema.org/GeoCoordinates"
                >
                    <meta
                        content={latitude}
                        itemProp="latitude"
                    />
                    <meta
                        content={longitude}
                        itemProp="longitude"
                    />
                    {this.maybeRenderMapThumbnail()}
                    <LocationCardInfo
                        data-test="location-card-info"
                        itemProp="address"
                        itemScope
                        itemType="http://schema.org/PostalAddress"
                    >
                        {this.maybeRenderPracticeName()}
                        <LocationName data-test="location-name">{name}</LocationName>
                        {this.renderAddress()}
                        {this.renderCityStateZip()}
                        <meta
                            content="USA"
                            itemProp="addressCountry"
                        />
                        {this.maybeRenderShowMoreShowLess()}
                    </LocationCardInfo>
                </div>
            </StyledLocationCard>
        );
    }
}

const StyledLocationCard = styled.div`
    width: 100%;
`;

const MapContainer = styled.div`
    display: inline-block;
    user-select: none;
    margin-right: ${mapMarginMobile}px;

    ${breakpoint.small} {
        margin-right: ${mapMarginDesktop}px;
    }
`;

const LocationCardInfo = styled.div`
    box-sizing: border-box;
    display: inline-block;
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    vertical-align: top;
    width: 100%;
    color: ${p => p.theme.profile.textColor};

    ${MapContainer} + & {
        font: 13px/20px ${FontFamily.SharpSansSemibold};
        width: calc(100% - ${MAP_SIZE_MOBILE + mapMarginMobile}px);

        ${breakpoint.small} {
            width: calc(100% - ${MAP_SIZE_DESKTOP + mapMarginDesktop}px);
        }

        ${breakpoint.medium} {
            font: 15px/24px ${FontFamily.SharpSansSemibold};
        }
    }
`;

const MobileMap = styled.div`
    ${breakpoint.small} {
        display: none;
    }
`;

const DesktopMap = styled.div`
    display: none;

    ${breakpoint.small} {
        display: block;
    }
`;

const LocationName = styled.p`
    font: 15px/20px ${FontFamily.SharpSansBold};
    ${breakpoint.medium} {
        line-height: 24px;
    }
`;

const Button = styled.button`
    ${linkStyle};
`;

export const LocationCardView = withTheme(LocationCard);
